import { DateTime } from "luxon";
import { FleetManagementRow, VehicleBookingEventDto } from "../../types/FleetManagement-row.types";
import { VehicleBlockingEvent } from "../../types/FleetManagement.types";
import { FleetCalendarEvent } from "../FleetManagementEventDetailDialog.component";
import { t } from "i18next";
import { getUserTitle } from "./getUserTitle.function";

export function hslToHex(hue: number, saturation: number, lightness: number): string {
  const s = saturation / 100;
  const l = lightness / 100;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const normalizedHue = ((hue % 360) + 360) % 360;
  const x = chroma * (1 - Math.abs(((normalizedHue / 60) % 2) - 1));
  const m = l - chroma / 2;

  let rPrime = 0;
  let gPrime = 0;
  let bPrime = 0;

  if (normalizedHue < 60) {
    rPrime = chroma;
    gPrime = x;
  } else if (normalizedHue < 120) {
    rPrime = x;
    gPrime = chroma;
  } else if (normalizedHue < 180) {
    gPrime = chroma;
    bPrime = x;
  } else if (normalizedHue < 240) {
    gPrime = x;
    bPrime = chroma;
  } else if (normalizedHue < 300) {
    rPrime = x;
    bPrime = chroma;
  } else {
    rPrime = chroma;
    bPrime = x;
  }

  const toHex = (value: number): string => {
    const rgbValue = Math.round((value + m) * 255);
    return rgbValue.toString(16).padStart(2, "0");
  };

  return `#${toHex(rPrime)}${toHex(gPrime)}${toHex(bPrime)}`;
}

export function getBookingColorByVehicleId(vehicleId: number): string {
  const id = Math.abs(vehicleId);
  // Spread colors across hue with subtle variation in saturation/lightness.
  const hue = Math.floor((id * 137.508) % 360);
  const saturation = 68 + (id % 12);
  const lightness = 46 + ((id * 7) % 10);
  return hslToHex(hue, saturation, lightness);
}

/**
 * Maps API blocking events to calendar events (for calendar view when using blocking-events API).
 */
export function mapBlockingEventsToFleetCalendarEvents(
  blockingEvents: VehicleBlockingEvent[],
  timezone: string
): FleetCalendarEvent[] {
  return blockingEvents
    .map(be => {
      const startDate = DateTime.fromISO(be.start).setZone(timezone);
      const endDate = DateTime.fromISO(be.end).setZone(timezone);
      if (!startDate.isValid || !endDate.isValid) return null;
      return {
        id: `block-${be.id}`,
        title: t("FleetEventBlocked"),
        start: startDate.toISO(),
        end: endDate.toISO(),
        color: "#B20000",
        editable: false,
        originalData: be
      } as FleetCalendarEvent;
    })
    .filter((e): e is FleetCalendarEvent => e != null);
}

/**
 * Maps API booking events to calendar events (for calendar view when using booking-events API).
 */
export function mapBookingEventsToFleetCalendarEvents(
  bookingEvents: VehicleBookingEventDto[],
  vehicleId: number,
  timezone: string
): FleetCalendarEvent[] {
  return bookingEvents
    .map((event, index) => {
      const startDate = DateTime.fromISO(event.startTime).setZone(timezone);
      const endDate = DateTime.fromISO(event.endTime).setZone(timezone);
      if (!startDate.isValid || !endDate.isValid) return null;
      return {
        id: `booking-${vehicleId}-${index}`,
        title: getUserTitle(event.userInfo, t("Unknown user")),
        start: startDate.toISO(),
        end: endDate.toISO(),
        color: "#1616E5",
        editable: false,
        originalData: event
      } as FleetCalendarEvent;
    })
    .filter((e): e is FleetCalendarEvent => e != null);
}

export function mapFleetEventSchedules({
  selectedRow,
  timezone
}: {
  selectedRow: FleetManagementRow;
  timezone: string;
}): FleetCalendarEvent[] {
  const newEvents: FleetCalendarEvent[] = [];

  // Map Bookings
  if (selectedRow.occupiedBooking) {
    selectedRow.occupiedBooking.forEach((event, index) => {
      const startDate = DateTime.fromISO(event.startTime).setZone(timezone);
      const endDate = DateTime.fromISO(event.endTime).setZone(timezone);

      if (startDate.isValid && endDate.isValid) {
        newEvents.push({
          id: `booking-${selectedRow.id}-${index}`,
          title: getUserTitle(event.userInfo, t("Unknown user")),
          start: startDate.toISO(),
          end: endDate.toISO(),
          color: "#1616E5",
          editable: false,
          originalData: event
        } as FleetCalendarEvent);
      }
    });
  }

  // Map Blocked Events
  if (selectedRow.blockedEvents) {
    selectedRow.blockedEvents.forEach((event, index) => {
      const startDate = DateTime.fromISO(event.start).setZone(timezone);
      const endDate = DateTime.fromISO(event.end).setZone(timezone);

      if (startDate.isValid && endDate.isValid) {
        newEvents.push({
          id: `block-${selectedRow.id}-${index}`,
          title: t("FleetEventBlocked"),
          start: startDate.toISO(),
          end: endDate.toISO(),
          color: "#B20000",
          editable: false,
          originalData: {
            id: -index,
            vehicleInventoryId: selectedRow.id,
            start: event.start,
            end: event.end,
            reason: event.reason ?? null
          }
        } as FleetCalendarEvent);
      }
    });
  }

  return newEvents;
}
